import React, { useCallback, useEffect, useState } from 'react';
import { Scaffold } from 'layouts';
import { exportToCSV } from 'utils/exportToCsv';
import { useCheckList } from 'hooks/api/useCheckList';
import { useToast } from 'components/ui/use-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FileText, Loader2 } from 'lucide-react';
import { useColumns } from './columns';
import { DataTable } from 'components/ui/DataTable';
import { SheetIcon } from 'lucide-react';
import { Button } from 'components/ui/button';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getChecklistInfo } from '../checklListUtils';
import { InnerOptions } from '../InnerOptions';
import { NavigatorLayout } from '../../../components/ui/navigator-layout';
import { ChecklistFilter } from '../Filter/index';

export const LoadingRejected: React.FC = () => {
  const { getCheckListAvulsos, getCheckListAvulsosReport } = useCheckList();

  const { options } = InnerOptions();
  const { toast } = useToast();
  const { columns } = useColumns();
  const [query, setQuery] = useState({});
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['getCheckListAvulsos', query, 'rejected'],
    queryFn: () =>
      getCheckListAvulsos({
        ...query,
        checklist_status: 'rejected',
      }),
    enabled: false,
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao carregar lista de checklist',
        duration: 5000,
      });
    },
  });

  const handleDatatablePagination = React.useCallback(
    async (pagination: any) => {
      setQuery((old) => ({ ...old, ...pagination }));
    },
    []
  );
  const formatDate = (date?: string | Date | null) => {
    if (!date) return '---';
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return '';
    return format(parsed, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  const formatRecusaMotivo = (report: any): string => {
    const infos = getChecklistInfo(report, 'recusa');
    if (Array.isArray(infos)) {
      return infos.length ? infos.join('\n') : '-';
    }
    return infos || '-';
  };

  const generationReport = useCallback((reportData: any) => {
    const lines = [];
    for (const report of reportData) {
      const links = getChecklistInfo(report, 'img');
      const line: { [key: string]: any } = {
        'ID': report.id,
        'Status do Checklist': getChecklistInfo(report, 'status') || '---',
        'Transportadora': getChecklistInfo(report, 'transportadora') || '---',
        'Placas': getChecklistInfo(report, 'placas') || '---',
        'Usuário': report?.user?.name || '---',
        'Tipo': getChecklistInfo(report, 'tipo') || '---',
        'Tipo do Veículo': getChecklistInfo(report, 'tipoVeiculo') || '---',
        'Agendamento': getChecklistInfo(report, 'agendamento') || '---',
        'Motorista': getChecklistInfo(report, 'motorista') || '---',
        'Início do Checklist': report.started_at
          ? formatDate(report.started_at)
          : '---',
        'Conclusão do Checklist': report.finished_at
          ? formatDate(report.finished_at)
          : '---',
        'Criado em': report.created_at ? formatDate(report.created_at) : '---',
        'Motivo da recusa - Item Reprobatório': formatRecusaMotivo(report),
        'Tempo do Checklist': getChecklistInfo(report, 'tempo') || '---',
      };

      // Adiciona cada link como um campo separado
      links.forEach((link: string, index: number) => {
        const key = `Fotos${index + 1}`;
        line[key] = link;
      });
      lines.push(line);
    }
    exportToCSV(
      lines,
      `checklist-avulsos-reprovados-${new Date().getTime()}`
    );
  }, []);

  const reportMutation = useMutation(getCheckListAvulsosReport, {
    onSuccess: async (data) => {
      await queryClient.invalidateQueries('getCheckListAvulsosReport');
      const lines = data?.checklists || [];
      if (lines.length) {
        generationReport(lines);
      } else {
        toast({
          variant: 'default',
          title: 'Aviso',
          description: 'Nada para exportar',
          duration: 5000,
        });
      }
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao requisitar relatório',
        duration: 5000,
      });
    },
  });

  const onExport = useCallback(() => {
    reportMutation.mutate({
      ...query,
      checklist_status: "rejected",
      all: true,
    });
  }, [query, reportMutation]);

  useEffect(() => {
    queryClient.removeQueries(['getCheckListAvulsos', query, 'rejected']);
    refetch();
  }, [query, queryClient, refetch]);

  return (
    <Scaffold>
      <NavigatorLayout options={options}>
        <section className="w-full h-auto pb-20">
          <ChecklistFilter onFilter={setQuery} query={query}>
            <div className="flex items-center gap-4">
              <FileText className="w-6 h-6 text-slate-500" />
              <h1 className="font-GilroyBold text-lg text-slate-700">
                {`Checklist Avulso - Reprovados`}
              </h1>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-[100px] p-0 ml-auto mt-[6px] bg-green-600 font-GilroySemibold text-white hover:bg-green-500 hover:text-white duration-300"
              onClick={() => onExport()}
              disabled={reportMutation.isLoading}
            >
              {reportMutation.isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <SheetIcon className="mr-2 h-4 w-4" />
              )}
              Exportar
            </Button>
          </ChecklistFilter>

          <DataTable
            data={data?.checklists || []}
            columns={columns}
            showControlColumns={false}
            isLoading={isLoading}
            noWrapHeaders
            noWrapCells
            tableClassName="min-w-[1700px]"
            pagination={{
              ...data?.pagination,
            }}
            onPaginationChange={handleDatatablePagination}
            manualPagination={true}
          />
        </section>
      </NavigatorLayout>
    </Scaffold>
  );
};
